/**
 * groupOrders — pure function fuer das Phase-7-Dashboard.
 *
 * UI-06: Type-4-Auftraege (Lieferauftraege) werden eingeklappt unter ihrem Type-1
 *        Hauptauftrag angezeigt (eine Listen-Position fuer beide).
 * UI-07: Verwaiste Type-4-Auftraege (Hauptauftrag NICHT in aktueller Liste, z.B.
 *        ausserhalb Tracking-Zeitraum) werden als eigene Karte mit Cross-Link-Label
 *        angezeigt.
 *
 * Pure function: nur Array-/Map-Operationen, kein State, kein async.
 * Testbar in Vitest ohne DOM oder React.
 *
 * Backend liefert `parentInScope: boolean` pro Order — dieses Flag zeigt ob der
 * Parent in der aktuellen Page-Antwort vorhanden ist. Der Grouper verarbeitet die
 * flat-list und gibt eine GroupedOrder-Liste zurueck:
 *   - main = Type 1 ODER verwaister Type 4
 *   - children = Type 4 mit parentInScope=true (gehoeren zu einem main)
 *
 * Reihenfolge der mains entspricht der Reihenfolge der Input-Orders (Backend
 * sortiert nach order_received_at DESC, das Frontend uebernimmt die Reihenfolge).
 *
 * Dieses Interface ist ein Spiegelbild der Backend-Definition aus service.ts
 * (07-01). Kein direkter Backend-Import — Frontend und Backend sind getrennte
 * Packages. Bei Shape-Aenderungen im Backend muss dieses Interface mitgepflegt werden.
 */

export interface OrderListItem {
  id: number;
  plentyOrderId: string;             // BIGINT vom Backend serialisiert als string
  orderType: number;                 // 1 = Bestellung, 4 = Lieferauftrag
  parentPlentyOrderId: string | null;
  parentInScope: boolean;            // true wenn parent in aktueller Page-Antwort vorhanden
  orderReceivedAt: string;           // ISO
  contactId: string | null;
  orderStatusId: number | null;
  orderStatusName: string | null;
  paymentStatus: string | null;
  packageNumber: string | null;
  shippingProfileName: string | null;
  trackingStatus: string;
  lastEventAt: string | null;
  lastInternalStatus: string | null;
  hasOpenNotification: boolean;
  userNote: string | null;
  closedAt: string | null;
  // Phase 5: erweiterte Card-Felder (optional, da nicht alle Endpoints sie liefern)
  billingAddress?: Record<string, any> | null;
  shippingAddress?: Record<string, any> | null;
  tags?: Array<{ id: number; name: string }> | null;
  paymentMethodName?: string | null;
  orderSource?: string | null;
  shippingProfileId?: number | null;
  warenausgangBookedAt?: string | null;
  statusEnteredAt?: string | null;
  hasPalette?: boolean;
  items?: Array<{
    id: number;
    variationId: string;
    variationNumber: string | null;
    itemName: string | null;
    quantity: number | null;
  }>;
}

export interface GroupedOrder {
  main: OrderListItem;        // Type 1 ODER verwaister Type 4 (eigene Karte)
  children: OrderListItem[];  // Type 4 mit parentInScope=true (eingeklappt unter main)
}

/**
 * Wandelt eine flat OrderListItem-Liste in eine GroupedOrder-Liste um.
 *
 * Algorithmus (zwei Schritte, O(n)):
 *   1. Kinder-Map aufbauen: parentPlentyOrderId → alle Type-4-Kinder mit
 *      parentInScope=true. Nur Kinder mit gesetzter parentPlentyOrderId landen hier.
 *   2. Linearer Durchlauf: jede Order die NICHT in der childSet ist, wird main.
 *      Ihre Kinder werden aus der Map nachgeschlagen.
 *
 * Type-4-Orders mit parentInScope=false (verwaist) oder parentPlentyOrderId=null
 * landen nicht in der childrenMap und werden daher als eigene main-Gruppe ausgegeben.
 */
export function groupOrders(orders: OrderListItem[]): GroupedOrder[] {
  if (orders.length === 0) return [];

  // Schritt 1: Kinder-Map aufbauen
  // Key: parentPlentyOrderId (als String), Value: Array der Kinder
  const childrenMap = new Map<string, OrderListItem[]>();
  for (const order of orders) {
    if (order.orderType === 4 && order.parentInScope && order.parentPlentyOrderId != null) {
      const key = String(order.parentPlentyOrderId);
      const existing = childrenMap.get(key);
      if (existing) {
        existing.push(order);
      } else {
        childrenMap.set(key, [order]);
      }
    }
  }

  // Schritt 2: Set aller Kinder-IDs — diese werden nicht als main ausgegeben
  const childSet = new Set<number>();
  for (const arr of childrenMap.values()) {
    for (const child of arr) {
      childSet.add(child.id);
    }
  }

  // Schritt 3: Linearer Durchlauf — alles ausser childSet-Eintraegen wird main
  const result: GroupedOrder[] = [];
  for (const order of orders) {
    if (childSet.has(order.id)) continue;
    result.push({
      main: order,
      children: childrenMap.get(String(order.plentyOrderId)) ?? [],
    });
  }

  return result;
}
